import {v4 as uuidv4} from 'uuid';

import {assert} from 'Asserts';
import {ClientId} from 'notebook/ELNConstants';
import {downloadCSVActionFunction} from 'notebook/structured-tables/action-functions/downloadCSVActionFunction';
import GenericStructuredTableAdapter from 'notebook/structured-tables/GenericStructuredTableAdapter';
import {getStructuredTableAdapters} from 'notebook/structured-tables/StructuredTableAdapters';
import {
  ActionFunctions,
  BaseStructuredTable,
  CreateStructuredTableProps,
  LastSyncedUpstreamValue,
  SchemaAndColumnGroup,
  StructuredTableAdapterOptions,
  StructuredTableColumn,
  StructuredTableKind,
  StructuredTableRow,
  StructuredTableSchema,
} from 'notebook/structured-tables/StructuredTableConstants';
import {makeStructuredTableColumn} from 'notebook/structured-tables/util/StructuredTableColumnUtil';
import {StructuredTableInsertionContextValue} from 'notebook/structured-tables/util/StructuredTableInsertionContext';
import {Cell} from 'notebook/tables/constants/CellConstants';
import {TableColumnType} from 'notebook/tables/constants/TableColumnConstants';
import {Table, TableReorderLocks} from 'notebook/tables/constants/TableConstants';
import {TableDimension} from 'notebook/tables/constants/TableSelectionConstants';
import {EvaluatedCellError} from 'notebook/tables/excess/Evaluator';
import {BaseTypedValueType, TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import {ProcedureSchemaFieldTypeTypedValue} from 'notebook/tables/typed-values/TypedValueTypes';
import {makeEmptyMaterialOutputSetting} from 'procedures/design/method-config/MaterialOutputConfigTable';
import {PARAMETER_TABLES_COLUMN_WIDTHS} from 'procedures/design/ProcedureDesignConstants';
import {
  ProcedureValidationError,
  ProcedureValidationErrorType,
  ProcedureValidationObjectType,
} from 'procedures/design/validation/ProcedureValidationTypes';
import {filterProcedureValidationErrors} from 'procedures/design/validation/procedureValidationUtils';
import {
  MaterialFlow,
  ParameterUsage,
  ParameterUsages,
  ProcedureMaterialOutputSetting,
} from 'procedures/ProcedureDomainTypes';
import {
  MaterialOutputType,
  STARTER_MATERIAL_OUTPUT_TYPE,
} from 'procedures/shared-components/schema-field-type-selectors/procedureSchemaFieldConstants';
import {
  convertMaterialTypeToTypedValue,
  convertTypedValueToMaterialType,
  doesTypedValueHaveLinkedSchema,
} from 'procedures/shared-components/schema-field-type-selectors/procedureSchemaFieldTypeConversionUtils';
import {
  BaseParameterConfigTableMetadata,
  getParameterUsageColumn,
  getParameterUsagesForId,
  makeParameterUsageCell,
} from 'procedures/tables/parameterTableAdapterParamUsageUtils';
import {
  cellText,
  getUserAnnotationIndicator,
  makeStringCell,
  validationErrorsToEvaluatedCellError,
} from 'procedures/tables/procedureTableAdapterUtils';
import {ProcedureSchemaFieldTypeContext} from 'procedures/tables/ProcedureTableConstants';
import {FieldType} from 'schemas/fields/SchemaFieldConstants';
import {SchemaFieldSelector} from 'schemas/SchemaFieldSelectorConstants';
import {Optional} from 'util/Types';

import {TagSchemaListingWithFieldsGraphQL} from '../../../graphql-fragments/schemas/__generated/TagSchemaListingWithFieldsGraphQL';

export interface ProcedureMaterialOutputsTableMetadata extends BaseParameterConfigTableMetadata {}

export interface ProcedureMaterialOutputsTableRowMetadata {
  apiId: string | null;
  procedureMaterialOutputId: string | null;
}

export enum MaterialOutputConfigColumn {
  NAME = 'NAME',
  TYPE = 'TYPE',
  MAPPED_TO = 'MAPPED_TO',
  DESCRIPTION = 'DESCRIPTION',
  CONFIRMED_IN = 'CONFIRMED_IN',
}

const ALL_POSSIBLE_COLUMNS_ORDERED = [
  MaterialOutputConfigColumn.NAME,
  MaterialOutputConfigColumn.TYPE,
  MaterialOutputConfigColumn.MAPPED_TO,
  MaterialOutputConfigColumn.DESCRIPTION,
  MaterialOutputConfigColumn.CONFIRMED_IN,
];

export function columnProvenanceId(column: MaterialOutputConfigColumn): string {
  return `mo_${column}`;
}

const GET_STC_BY_COLUMN: Record<
  MaterialOutputConfigColumn,
  (metadata: ProcedureMaterialOutputsTableMetadata) => StructuredTableColumn
> = {
  [MaterialOutputConfigColumn.NAME]: () => ({
    ...makeStructuredTableColumn({
      columnType: TableColumnType.STRING,
      name: 'Name',
      isRequired: false,
      width: PARAMETER_TABLES_COLUMN_WIDTHS.WIDE,
    }),
    provenanceUuid: columnProvenanceId(MaterialOutputConfigColumn.NAME),
  }),
  [MaterialOutputConfigColumn.TYPE]: () => ({
    ...makeStructuredTableColumn({
      columnType: TableColumnType.PROCEDURE_SCHEMA_FIELD_TYPE,
      name: 'Material type',
      isRequired: true,
      width: PARAMETER_TABLES_COLUMN_WIDTHS.WIDE,
    }),
    columnType: TableColumnType.PROCEDURE_SCHEMA_FIELD_TYPE, // for TS
    provenanceUuid: columnProvenanceId(MaterialOutputConfigColumn.TYPE),
    context: ProcedureSchemaFieldTypeContext.MATERIAL_OUTPUT,
  }),
  [MaterialOutputConfigColumn.MAPPED_TO]: () => ({
    ...makeStructuredTableColumn({
      columnType: TableColumnType.STRING,
      name: 'Mapped to',
      isRequired: false,
      isReadOnly: true,
      width: PARAMETER_TABLES_COLUMN_WIDTHS.WIDE,
    }),
    provenanceUuid: columnProvenanceId(MaterialOutputConfigColumn.MAPPED_TO),
  }),
  [MaterialOutputConfigColumn.DESCRIPTION]: () => ({
    ...makeStructuredTableColumn({
      columnType: TableColumnType.STRING,
      name: 'Description',
      isRequired: false,
      width: PARAMETER_TABLES_COLUMN_WIDTHS.WIDE,
    }),
    provenanceUuid: columnProvenanceId(MaterialOutputConfigColumn.DESCRIPTION),
  }),
  [MaterialOutputConfigColumn.CONFIRMED_IN]: (metadata) =>
    getParameterUsageColumn({
      metadata,
      provenanceUuid: columnProvenanceId(MaterialOutputConfigColumn.CONFIRMED_IN),
    }),
};

export function getColumnIndex(
  targetColumn: MaterialOutputConfigColumn,
  stColumns: Array<StructuredTableColumn>
): number {
  return stColumns.findIndex(({provenanceUuid}) => provenanceUuid === columnProvenanceId(targetColumn));
}

/**
 * This table adapter is for configuration of material outputs
 */
export type ProcedureMaterialOutputsTable = BaseStructuredTable<
  ProcedureMaterialOutputsTableMetadata,
  ProcedureMaterialOutputsTableRowMetadata,
  ClientId
>;

export default class ProcedureMaterialOutputsTableAdapter extends GenericStructuredTableAdapter<
  ProcedureMaterialOutputsTableMetadata,
  ProcedureMaterialOutputsTableRowMetadata
> {
  constructor(options?: StructuredTableAdapterOptions) {
    super(StructuredTableKind.PROCEDURE_MATERIAL_OUTPUTS, {allowNoRowsInEntries: true, ...options});
  }

  override getActionFunctions(
    tableMetadata: ProcedureMaterialOutputsTableMetadata,
    schemas: Array<StructuredTableSchema>
  ): ActionFunctions<ProcedureMaterialOutputsTableMetadata, ProcedureMaterialOutputsTableRowMetadata> {
    return {secondary: [downloadCSVActionFunction]};
  }

  getDisplayName(table: ProcedureMaterialOutputsTable): string {
    return '';
  }

  getRowDisplayName(_: ProcedureMaterialOutputsTable): string {
    return 'material';
  }

  getBlankRowMetadata(
    tableMetadata: ProcedureMaterialOutputsTableMetadata
  ): ProcedureMaterialOutputsTableRowMetadata {
    return {apiId: null, procedureMaterialOutputId: null};
  }

  static getAdapter(): ProcedureMaterialOutputsTableAdapter {
    return getStructuredTableAdapters()[
      StructuredTableKind.PROCEDURE_MATERIAL_OUTPUTS
    ] as ProcedureMaterialOutputsTableAdapter;
  }

  override allowPasteTable(): boolean {
    return false;
  }

  override allowDeleteTable(_: ProcedureMaterialOutputsTable): {
    canDelete: boolean;
    reason?: string;
  } {
    return {canDelete: false};
  }

  override _getAllSupportedColumns({
    entryId,
    metadata,
    schemas,
    selectorsById,
    structuredTableInsertionContext,
  }: {
    entryId: string;
    metadata: ProcedureMaterialOutputsTableMetadata;
    schemas: Array<SchemaAndColumnGroup>;
    selectorsById: {[selectorId: string]: SchemaFieldSelector};
    structuredTableInsertionContext: StructuredTableInsertionContextValue;
  }): Array<StructuredTableColumn> {
    return ALL_POSSIBLE_COLUMNS_ORDERED.map((c) => GET_STC_BY_COLUMN[c](metadata));
  }

  // We don't lock any columns in a finalized row
  override getNonFinalizingColumnTypes(): Array<TableColumnType> {
    return [
      ...super.getNonFinalizingColumnTypes(),
      TableColumnType.STRING,
      TableColumnType.PROCEDURE_SCHEMA_FIELD_TYPE,
      TableColumnType.PROCEDURE_PARAMETER_CONFIRMED_IN,
    ];
  }

  static isMaterialSchemaSpecified(
    columns: Array<StructuredTableColumn>,
    row: StructuredTableRow<ProcedureMaterialOutputsTableRowMetadata>
  ): boolean {
    const materialTypeCell = row.cells[getColumnIndex(MaterialOutputConfigColumn.TYPE, columns)];
    return doesTypedValueHaveLinkedSchema(materialTypeCell.typedValue);
  }

  // Check against all user-entered fields. Will never report "empty" if there is a backing model;
  // this deliberately prevents users from blanking out fields on an existing object.
  static isMaterialOutputEmpty(mo: ProcedureMaterialOutputSetting): boolean {
    return (
      mo.materialOutputVersion.id == null &&
      mo.materialOutputVersion.name.length === 0 &&
      mo.materialOutputVersion.description.length === 0 &&
      mo.materialOutputVersion.dataType.requiredLink.tagSchema == null
    );
  }

  override skipUnsubmittedIndicatorsForRow(
    structuredTable: ProcedureMaterialOutputsTable,
    row: StructuredTableRow<ProcedureMaterialOutputsTableRowMetadata>
  ): boolean {
    // Check first if a material schema is specified because rowToMaterialOutputSetting will error if we pass
    // in an empty array of tag schemas and the material type has a linked schema
    if (ProcedureMaterialOutputsTableAdapter.isMaterialSchemaSpecified(structuredTable.columns, row)) {
      return false;
    }
    const setting = ProcedureMaterialOutputsTableAdapter.rowToMaterialOutputSetting(
      structuredTable.columns,
      row,
      []
    );
    return ProcedureMaterialOutputsTableAdapter.isMaterialOutputEmpty(setting);
  }

  private static makeMaterialTypeCell(
    materialOutputSetting: ProcedureMaterialOutputSetting,
    isMapped: boolean,
    isProcedurePoW: boolean,
    error: Optional<EvaluatedCellError> = null
  ): Cell {
    // Don't lock cells for PoW procedures because we don't self-heal the material type on edge creation, so
    // users should be able to fix data flow edge validation errors.
    const shouldLock = isMapped && !isProcedurePoW;
    const userAnnotationIndicator = getUserAnnotationIndicator(
      shouldLock
        ? 'This material is passed to a subsequent unit operation. In order to set a different type, ' +
            'you must delete the arrow linking the materials.'
        : null
    );
    return {
      text: '',
      typedValue: convertMaterialTypeToTypedValue(materialOutputSetting.materialOutputVersion.dataType),
      locked: shouldLock,
      userAnnotationIndicator,
      // Let the annotation take priority
      error: userAnnotationIndicator == null ? error : null,
    };
  }

  static makeCellFromMaterialOutput(
    materialOutputSetting: ProcedureMaterialOutputSetting,
    column: StructuredTableColumn,
    materialMapping: MaterialFlow | undefined,
    validationErrors: Array<ProcedureValidationError>,
    parameterUsages: Array<ParameterUsage>,
    isProcedurePoW: boolean
  ): Cell {
    switch (column.provenanceUuid) {
      // Name
      case columnProvenanceId(MaterialOutputConfigColumn.NAME): {
        const namingValidationErrors = filterProcedureValidationErrors(validationErrors, {
          errorTypes: [
            ProcedureValidationErrorType.NON_UNIQUE_NAME,
            ProcedureValidationErrorType.INVALID_NAME,
          ],
        });

        return makeStringCell(materialOutputSetting.materialOutputVersion.name, {
          error: validationErrorsToEvaluatedCellError(
            materialOutputSetting.materialOutputVersion.cid,
            ProcedureValidationObjectType.MATERIAL_OUTPUT,
            namingValidationErrors
          ),
        });
      }

      // Mapped to
      case columnProvenanceId(MaterialOutputConfigColumn.MAPPED_TO):
        return makeStringCell(
          materialMapping
            ? `${materialMapping.targetMethodName}: ${materialMapping.targetMaterialName}`
            : null
        );

      // Description
      case columnProvenanceId(MaterialOutputConfigColumn.DESCRIPTION):
        return makeStringCell(materialOutputSetting.materialOutputVersion.description);

      // material type
      case columnProvenanceId(MaterialOutputConfigColumn.TYPE): {
        const materialOutputTypeErrors = filterProcedureValidationErrors(validationErrors, {
          errorTypes: [
            ProcedureValidationErrorType.MISSING_MATERIAL_OUTPUT_TYPE,
            ProcedureValidationErrorType.ARCHIVED_SCHEMA,
            ProcedureValidationErrorType.INVALID_SCHEMA_FOR_PARAMETER_TYPE,
          ],
        });
        return ProcedureMaterialOutputsTableAdapter.makeMaterialTypeCell(
          materialOutputSetting,
          materialMapping != null,
          isProcedurePoW,
          validationErrorsToEvaluatedCellError(
            materialOutputSetting.materialOutputVersion.cid,
            ProcedureValidationObjectType.MATERIAL_OUTPUT,
            materialOutputTypeErrors
          )
        );
      }
      case columnProvenanceId(MaterialOutputConfigColumn.CONFIRMED_IN):
        return makeParameterUsageCell(parameterUsages);
      default:
        assert(false, 'Unknown table column', {column});
    }
  }

  static materialOutputSettingToRow(
    materialOutputSetting: ProcedureMaterialOutputSetting,
    table: ProcedureMaterialOutputsTable,
    rowIndex: number,
    validationErrors: Array<ProcedureValidationError>,
    materialFlows: Array<MaterialFlow>,
    parameterUsages: Array<ParameterUsage>,
    isProcedurePoW: boolean,
    cellLockedMessage?: Optional<string>
  ): StructuredTableRow<ProcedureMaterialOutputsTableRowMetadata> {
    const adapter = ProcedureMaterialOutputsTableAdapter.getAdapter();
    const materialMapping = materialFlows.find(
      ({sourceMaterialCid}) => sourceMaterialCid === materialOutputSetting.materialOutputVersion.cid
    );

    return {
      ...adapter.getBlankRow(table.metadata, rowIndex, table.columns),
      rowId: materialOutputSetting.materialOutputVersion.cid ?? uuidv4(),
      metadata: {
        apiId: materialOutputSetting.materialOutputVersion.id,
        procedureMaterialOutputId: materialOutputSetting.materialOutputVersion.procedureMaterialOutputId,
      },
      cells: table.columns.map((column) =>
        ProcedureMaterialOutputsTableAdapter.makeCellFromMaterialOutput(
          materialOutputSetting,
          column,
          materialMapping,
          validationErrors,
          parameterUsages,
          isProcedurePoW
        )
      ),
    };
  }

  private static isProcedureSchemaFieldTypeTypedValue(
    typedValue: TypedValue
  ): typedValue is ProcedureSchemaFieldTypeTypedValue {
    return typedValue.type === BaseTypedValueType.PROCEDURE_SCHEMA_FIELD_TYPE;
  }

  private static isBioentityLinkFieldType(fieldType: FieldType): fieldType is FieldType.BIOENTITY_LINK {
    return fieldType === FieldType.BIOENTITY_LINK;
  }

  static forgivinglyDetermineMaterialTypeFromCell(
    cell: Cell,
    tagSchemas: Array<TagSchemaListingWithFieldsGraphQL>
  ): MaterialOutputType {
    const typedValue = cell.typedValue;
    if (
      typedValue == null ||
      !ProcedureMaterialOutputsTableAdapter.isProcedureSchemaFieldTypeTypedValue(typedValue) ||
      !ProcedureMaterialOutputsTableAdapter.isBioentityLinkFieldType(
        typedValue.fieldDefinition.fieldType as FieldType
      )
    ) {
      return STARTER_MATERIAL_OUTPUT_TYPE;
    }

    const materialType = convertTypedValueToMaterialType(typedValue, tagSchemas) as MaterialOutputType;
    if (materialType?.type !== FieldType.BIOENTITY_LINK) {
      return STARTER_MATERIAL_OUTPUT_TYPE;
    }
    return materialType;
  }

  static makeLastSyncedUpstreamValues(
    this: void,
    materialOutputSetting: ProcedureMaterialOutputSetting,
    row: StructuredTableRow<ProcedureMaterialOutputsTableRowMetadata>,
    columns: Array<StructuredTableColumn>,
    parameterUsages: Array<ParameterUsage>,
    isProcedurePoW: boolean
  ): Array<LastSyncedUpstreamValue> {
    const submittedAt = new Date().toISOString();
    return columns.map((column) => ({
      columnGroup: null,
      columnType: column.columnType,
      columnProvenanceUuid: column.provenanceUuid,
      submittedAt,
      fieldId: null,
      value:
        ProcedureMaterialOutputsTableAdapter.makeCellFromMaterialOutput(
          materialOutputSetting,
          column,
          undefined,
          [],
          parameterUsages,
          isProcedurePoW
        ).typedValue ?? null,
    }));
  }

  static rowToMaterialOutputSetting(
    columns: Array<StructuredTableColumn>,
    row: StructuredTableRow<ProcedureMaterialOutputsTableRowMetadata>,
    tagSchemas: Array<TagSchemaListingWithFieldsGraphQL>
  ): ProcedureMaterialOutputSetting {
    assert(
      columns.length === row.cells.length,
      'Expected row to have same number of cells as number of columns'
    );

    const materialType = ProcedureMaterialOutputsTableAdapter.forgivinglyDetermineMaterialTypeFromCell(
      row.cells[getColumnIndex(MaterialOutputConfigColumn.TYPE, columns)],
      tagSchemas
    );

    return {
      materialOutputVersion: {
        cid: row.rowId,
        id: row.metadata.apiId,
        procedureMaterialOutputId: row.metadata.procedureMaterialOutputId,
        name: cellText(row.cells[getColumnIndex(MaterialOutputConfigColumn.NAME, columns)]).trim(),
        description: cellText(
          row.cells[getColumnIndex(MaterialOutputConfigColumn.DESCRIPTION, columns)]
        ).trim(),
        dataType: materialType,
      },
    };
  }

  override getBlankRow(
    tableMetadata: ProcedureMaterialOutputsTableMetadata,
    rowIndex: number,
    columns: Array<StructuredTableColumn>,
    initialRowMetadata?: ProcedureMaterialOutputsTableRowMetadata
  ): StructuredTableRow<ProcedureMaterialOutputsTableRowMetadata> {
    const row = super.getBlankRow(tableMetadata, rowIndex, columns, initialRowMetadata);
    const cells = row.cells.map((cell, colIndex) =>
      colIndex === getColumnIndex(MaterialOutputConfigColumn.TYPE, columns)
        ? ProcedureMaterialOutputsTableAdapter.makeMaterialTypeCell(
            makeEmptyMaterialOutputSetting(),
            false,
            false
          )
        : cell
    );
    return {
      ...row,
      cells,
    };
  }

  override toTable(materialsTable: ProcedureMaterialOutputsTable): Table {
    const table = super.toTable(materialsTable);
    return {
      ...table,
      lockedDims: {allowAddRows: true, allowRemoveRows: true, allowEditCols: false},
      allowColumnEdits: false,
    };
  }

  createTableFromMaterials(
    createProps: CreateStructuredTableProps<ProcedureMaterialOutputsTableMetadata>,
    tableCid: string,
    materialOutputSettings: Array<ProcedureMaterialOutputSetting>,
    materialFlows: Array<MaterialFlow>,
    procedureValidationErrors: Array<ProcedureValidationError>,
    parameterUsagesByParameterVersionId: ParameterUsages,
    isProcedurePow: boolean
  ): ProcedureMaterialOutputsTable {
    const baseTable = {
      ...this.createTable(createProps),
      cid: tableCid,
    };
    return {
      ...baseTable,
      rows: materialOutputSettings.map((materialOutputSetting, index) =>
        ProcedureMaterialOutputsTableAdapter.materialOutputSettingToRow(
          materialOutputSetting,
          baseTable,
          index,
          procedureValidationErrors,
          materialFlows,
          getParameterUsagesForId(
            parameterUsagesByParameterVersionId,
            materialOutputSetting.materialOutputVersion.id
          ),
          isProcedurePow,
          null
        )
      ),
    };
  }

  override allowReorderDimensions(): TableReorderLocks {
    return {
      [TableDimension.ROW]: true,
      [TableDimension.COLUMN]: false,
    };
  }
}
